import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { Zoom, toast } from 'react-toastify';
import Cookies from './Cookies';
import Auth from './Auth';
import Certificate from './Certificate';
import Plugins from './Plugins';
import Menu from './Menu';
import APIReestr from './Resstr';
import File from './File';
import DamaskAPI from './DamaskAPI';
import APIToast from './Toaste';
import APIService from './Service';
import { GarApi } from './Gar';
import CashApi from './Cash';
import APITemplate from './Template';
import EnterAPI from './EnterAPI';
import ReturnAPI from './ReturnAPI';
import FileWizardAPI from './FileWizard';
import { IResult, ResponseType } from 'src/Types';
import StepQuestionsService from './TStepQuestionsService';
import APIReport from './Reports';
import APIPreset from './Preset';
import APIReceiptArchive from './ReceiptArchive';
import API_Dashboard from './Dashboard';
import { CustomerItemType } from 'src/Types';
import APIAttachment from './Attachment';
import APITask from './Tasks';
import { oListItemType } from 'src/Types/ReDoc/FilterEnumList';
import Guid from 'src/Utils/Guid';

export class API_NEW {
	/**
	 * Базовый метод для проведения запросов ( внутри производится обработка запроса )
	 * @param cfg
	 * @returns
	 */
	static onRequest = async <T = null>(
		cfg: AxiosRequestConfig,
	): Promise<ResponseType<T>> => {
		// Безопасный разбор URL: спецсимволы/относительные пути могут кидать URIError
		let traceparent = Guid.create().toString();
		try {
			const base = window?.location?.origin || 'http://localhost';
			const parsedUrl = new URL(cfg.url ?? base, base);
			traceparent = parsedUrl.pathname + '_' + traceparent;
		} catch {
			// Фолбэк: только GUID, чтобы не ронять запросы
		}
		cfg.headers = {
			...cfg.headers,
			traceparent,
		};
		if (cfg.withCredentials === undefined) {
			cfg.withCredentials = true;
		}
		const res: ResponseType<T> = {
			statusCode: 400,
			errorMessage: 'Необработанная ошибка :(',
			hasError: true,
			result: undefined,
		};
		try {
			const response = await axios.request(cfg);
			res.statusCode = response.status;
			res.hasError = !!response.data.hasError;
			if (response.data.errorMessage) {
				res.errorMessage = response.data.errorMessage;
			}
			if (response.status === 200) {
				if (!response.data.hasError) {
					res.result = response.data as T;
					return res;
				}
			} else if ([204, 403].includes(response.status)) {
				res.errorMessage = '204';
				res.hasError = true;
				return res;
			}
		} catch (error: any) {
			console.groupCollapsed('Ошибка при выполнении запроса');
			console.log('Адрес - ', cfg.url);
			console.warn(error.message);
			console.warn(error);
			console.groupEnd();
			res.hasError = true;
			res.errorMessage = error.message;
			res.statusCode = error.response.status;
		} finally {
			return res;
		}
	};

	static showToast(
		isSuccess: boolean,
		message: string,
		timeout: number | false = 3,
	) {
		const func = isSuccess ? 'success' : 'error';
		const autoClose =
			!!timeout && !Number.isNaN(timeout) ? timeout * 1000 : false;
		toast[func](message, {
			position: 'top-center',
			autoClose,
			hideProgressBar: true,
			closeOnClick: true,
			className: isSuccess ? 'rdc_toast-success' : 'rdc_toast-error',
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: 'colored',
			transition: Zoom,
			closeButton: false,
		});
	}
}

class API {
	static Cookies = Cookies;
	static Auth = Auth;
	static Certificate = Certificate;
	static Plugins = Plugins;
	static Menu = Menu;
	static Reestr = APIReestr;
	static File = File;
	static DamaskEQ = DamaskAPI;
	static EnterEQ = EnterAPI;
	static ReturnEQ = ReturnAPI;
	static Toast = APIToast;
	static Service = APIService;
	static Gar = GarApi;
	static Cash = CashApi;
	static Template = APITemplate;
	static FileWizard = FileWizardAPI;
	static StepQuestions = StepQuestionsService;
	static Report = APIReport;
	static Preset = APIPreset;
	static Task = APITask;
	static ReceiptArchive = APIReceiptArchive;
	static Dashboard = API_Dashboard;
	static Attachment = APIAttachment;

	static prepareJSONResult = async <T>(
		request: Response,
	): Promise<IResult<T>> => {
		const result: IResult<T> = {
			success: true,
			message: '',
			data: null,
		};
		if (request.status === 200) {
			result.data = await request.json();
			return result;
		} else if (request.status === 401) {
			API.Auth.redirectToAuthorize();
			result.message =
				'Авторизация просрочена. Осуществляется переход на страницу авторизации.';
			return result;
		} else {
			result.success = false;
			result.message = `Ошибка при получении реестров. Сервер вернул - (${request.status}): ${request.statusText}`;
		}
		return result;
	};

	static checkUnauthorized = (
		request: Response,
		showMessage = false,
	): boolean => {
		if ([401, 403].includes(request.status)) {
			if (showMessage) {
				API.Toast.show(
					false,
					'Авторизация просрочена. Осуществляется переход на страницу авторизации.',
				);
			}
			API.Auth.redirectToAuthorize();
		}
		return false;
	};

	/**  */
	static getParticipants = async (
		searchTern = '',
		skipPageCount = 0,
		takeCount = 40,
	): Promise<CustomerItemType[]> => {
		try {
			const uri = `${window.host}/WebInterfaceModule/Search/GetParticipants?term=${searchTern}&skipPageCount=${skipPageCount}&takeCount=${takeCount}`;
			const result = await fetch(uri, { credentials: 'include' });
			if (result.status === 200) {
				const answer = await result.json();
				if (Array.isArray(answer)) {
					return answer;
				}
				return answer.list;
			}
			throw new Error('Не удалось получить список доступных фильтров!');
		} catch (e: any) {
			console.warn('Не удалось получить список доступных фильтров!', e);
			return [];
		}
	};

	/**  */
	static getParticipant = async (
		id: string,
	): Promise<CustomerItemType | null> => {
		try {
			const uri =
				window.host + '/WebInterfaceModule/Search/GetParticipantById';
			const frmData = new FormData();
			frmData.append('term', id);
			const result = await fetch(uri, {
				method: 'POST',
				credentials: 'include',
				body: frmData,
			});
			if (result.status === 200) {
				const answer = await result.json();
				return answer;
			}
			throw new Error('Не удалось получить список доступных фильтров!');
		} catch (e: any) {
			console.warn('Не удалось получить список доступных фильтров!', e);
			return null;
		}
	};

	/////////////////////////////////////////////////////////////////////////////////////

	/**   */
	static getFilterValues = async (
		method = 'get',
		url: string,
		searchTern = '',
		page = 0,
		limit = 40,
		current?: string | string[],
		additionData?: any,
	): Promise<any> => {
		return method === 'get'
			? this._getFilterValuesGet(
					url,
					searchTern,
					page,
					limit,
					current,
					additionData,
				)
			: this._getFilterValuesPost(
					url,
					searchTern,
					page,
					limit,
					additionData,
				);
	};

	static _getFilterValuesGet = async (
		url: string,
		searchTern = '',
		page = 0,
		limit = 40,
		current?: string | string[],
		additionData?: any,
	): Promise<oListItemType[]> => {
		try {
			let uri =
				window.host +
				`${url}?term=${searchTern}&page=${page}&limit=${limit}`;
			if (additionData) {
				for (const key in additionData) {
					if (
						Object.prototype.hasOwnProperty.call(additionData, key)
					) {
						const value = additionData[key];
						uri += `&${key}=${value}`;
					}
				}
			}
			if (current) {
				uri += `&currentId=${current}`;
			}

			const result = await fetch(uri, { credentials: 'include' });
			let list: oListItemType[] = [];
			if (result.status === 200) {
				const answer = await result.json();

				if (Array.isArray(answer)) {
					list = answer;
				}
				if (!!answer?.result && Array.isArray(answer?.result)) {
					list = answer.result;
				} else if (!!answer.list && Array.isArray(answer?.list)) {
					list = answer.list;
				}

				if (answer.currentItems?.length) {
					answer.currentItems.forEach((item: any) => {
						if (
							list.findIndex((i) => {
								return i.id === item.id;
							}) === -1
						) {
							list.push(item);
						}
					});
				}

				return list;
			}
			return list;
		} catch (e: any) {
			console.warn('Не удалось получить список доступных фильтров!', e);
			return [];
		}
	};

	static _getFilterValuesPost = async (
		url: string,
		searchTern = '',
		page = 0,
		limit = 40,
		additionData?: any,
	): Promise<any> => {
		const requestBody = {
			searchString: searchTern,
			page,
			limit,
			...additionData,
		};
		try {
			const uri = window.host + url;
			const result = await fetch(uri, {
				method: 'post',
				credentials: 'include',
				body: JSON.stringify(requestBody),
			});
			if (result.status === 200) {
				const answer = await result.json();

				if (Array.isArray(answer)) {
					return answer;
				}
				if (Array.isArray(answer?.list)) {
					return answer.list;
				}
				if (Array.isArray(answer?.result)) {
					return answer.result;
				}

				return answer;
			}
			throw new Error('Не удалось получить список доступных фильтров!');
		} catch (e: any) {
			console.warn('Не удалось получить список доступных фильтров!', e);
		}
	};

	static showToast(
		isSuccess: boolean,
		message: string,
		timeout: number | false = 3,
	) {
		const func = isSuccess ? 'success' : 'error';
		const autoClose =
			!!timeout && !Number.isNaN(timeout) ? timeout * 1000 : false;
		toast[func](message, {
			position: 'top-center',
			autoClose,
			hideProgressBar: true,
			closeOnClick: true,
			className: isSuccess ? 'rdc_toast-success' : 'rdc_toast-error',
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
			theme: 'colored',
			transition: Zoom,
			closeButton: false,
		});
	}
}

export default API;
